import React, { useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import { aiExperienceApplicationMessages } from '@atlaskit/editor-common/ai-messages';
import { ACTION } from '@atlaskit/editor-common/analytics';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';
import { DiscardScreen } from '@atlassian/generative-ai-modal/screens/Discard';

import { useContentComponentContext } from '../../../hooks/useContentComponentContext';
import { useRovoAbort } from '../../../hooks/useRovoDiscard';
import { convertExperienceName } from '../../../utils/analytics/analyticsFlow';
import { useFireAIAnalyticsEvent } from '../../../utils/analytics/utils';
import { Container } from '../Container';
import type { SessionUIComponentProps } from '../SessionUIComponent';
import type { Action } from '../useUIState';

export function DiscardSessionScreen({
	api,
	appearance,
	dispatch,
}: Pick<SessionUIComponentProps, 'api' | 'appearance'> & {
	dispatch: React.Dispatch<Action>;
}): React.JSX.Element {
	const { abort } = useRovoAbort();
	const { formatMessage } = useIntl();
	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
	const { isInDocumentStreamingEnabled } = useContentComponentContext();

	const onCancel = useCallback(() => {
		api?.aiExperience.actions.getAiExperienceActor()?.send({ type: 'disable discard' });
		dispatch({ type: 'cancel' });
	}, [api, dispatch]);

	const onDiscard = useCallback(() => {
		const context = api?.aiExperience.actions.getAiExperienceActor().getSnapshot().context;

		/**
		 * Introduced in EDF-1443 to track discard event confirmations
		 * separately to normal discard events
		 */
		fireAIAnalyticsEvent({
			// Simplified context from streamingApi to avoid issues
			// To follow-up
			payload: createUnifiedAnalyticsPayload({
				action: ACTION.DISMISSED,
				interactionID: context?.channelId,
				experienceName: convertExperienceName(context?.configItem.key.toString() ?? ''),
				isProactive: false,
				invokedFrom: 'streamingApi',
			}),
		});
		api?.aiExperience.actions.endAIExperience();
		// Abort any ongoing Rovo requests - this will end the session
		abort();
		dispatch({ type: 'terminate' });
	}, [api, dispatch, abort, fireAIAnalyticsEvent]);

	return (
		<Container>
			<DiscardScreen
				onCancel={onCancel}
				onDiscard={onDiscard}
				text={formatMessage(aiExperienceApplicationMessages.discardStreamingInDocumennt)}
				appearance={appearance}
				isAIFC={isInDocumentStreamingEnabled}
			/>
		</Container>
	);
}
